import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import RideCard from "@/components/RideCard";
import RideMap from "@/components/RideMap";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import { Search, MapPin, SlidersHorizontal } from "lucide-react";

export default function Feed() {
  const [catalog, setCatalog] = useState({ ride_types: [], bike_types: [] });
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    location: null,
    radius_km: 50,
    around_datetime: "",
    time_window_hours: 2,
    ride_type: "",
    bike_type: "",
    q: "",
  });

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data));
  }, []);

  const selectedLocation = filters.location;

  const fetchRides = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedLocation) {
        params.lat = selectedLocation.lat;
        params.lng = selectedLocation.lng;
        params.radius_km = filters.radius_km;
      }
      if (filters.around_datetime) {
        params.around_datetime = new Date(filters.around_datetime).toISOString();
        params.time_window_hours = filters.time_window_hours;
      }
      if (filters.ride_type) params.ride_type = filters.ride_type;
      if (filters.bike_type) params.bike_type = filters.bike_type;
      if (filters.target_level) params.target_level = filters.target_level;
      if (filters.q && filters.q.trim()) params.q = filters.q.trim();
      const { data } = await api.get("/rides", { params });
      setRides(data);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, filters.radius_km, filters.around_datetime, filters.time_window_hours, filters.ride_type, filters.bike_type, filters.target_level, filters.q]);

  useEffect(() => {
    fetchRides();
  }, [fetchRides]);

  const mapCenter = selectedLocation
    ? [selectedLocation.lat, selectedLocation.lng]
    : [42.5, 12.5]; // Italia

  return (
    <>
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="label-tag mb-2">Bicyclub Feed</div>
            <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter text-gray-900">
              I prossimi giri
            </h1>
          </div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500">
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span data-testid="feed-results-count">{rides.length} risultati</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          {/* Filters */}
          <aside className="tactical-card rounded-sm p-5 space-y-5 self-start lg:sticky lg:top-20">
            <div>
              <label className="label-tag block mb-2"><Search className="w-3 h-3 inline mr-1" /> Cerca per nome</label>
              <input
                data-testid="filter-q"
                type="search"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Es. Ghisallo, Lago di Como..."
                className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-3 py-2.5 text-sm text-gray-900"
              />
            </div>

            <div>
              <label className="label-tag block mb-2"><MapPin className="w-3 h-3 inline mr-1" /> Zona di partenza</label>
              <LocationAutocomplete
                testId="filter-location"
                value={filters.location}
                onChange={(loc) => setFilters({ ...filters, location: loc })}
                placeholder="Cerca città o paese..."
              />
            </div>

            <div>
              <label className="label-tag block mb-2">Raggio: {filters.radius_km} km</label>
              <input
                data-testid="filter-radius"
                type="range" min="10" max="300" step="10"
                value={filters.radius_km}
                onChange={(e) => setFilters({ ...filters, radius_km: Number(e.target.value) })}
                className="w-full accent-[#E11D48]"
                disabled={!filters.location}
              />
              <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                <span>10km</span><span>300km</span>
              </div>
            </div>

            <div>
              <label className="label-tag block mb-2">Quando</label>
              <input
                data-testid="filter-around-datetime" type="datetime-local"
                value={filters.around_datetime}
                onChange={(e) => setFilters({ ...filters, around_datetime: e.target.value })}
                className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-3 py-2.5 text-sm text-gray-900"
              />
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <button
                  type="button"
                  data-testid="filter-when-today"
                  onClick={() => {
                    const now = new Date();
                    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
                    setFilters((f) => ({ ...f, around_datetime: now.toISOString().slice(0, 16), time_window_hours: 4 }));
                  }}
                  className="text-[10px] uppercase tracking-wider font-bold text-gray-700 border border-gray-300 hover:border-gray-500 rounded-sm px-2 py-1"
                >
                  Oggi
                </button>
                <button
                  type="button"
                  data-testid="filter-when-allday"
                  onClick={() => {
                    const base = filters.around_datetime ? new Date(filters.around_datetime) : new Date();
                    base.setHours(12, 0, 0, 0);
                    base.setMinutes(base.getMinutes() - base.getTimezoneOffset());
                    setFilters((f) => ({ ...f, around_datetime: base.toISOString().slice(0, 16), time_window_hours: 12 }));
                  }}
                  className="text-[10px] uppercase tracking-wider font-bold text-white bg-[#E11D48] hover:bg-[#BE123C] rounded-sm px-2 py-1"
                >
                  Seleziona tutto il giorno
                </button>
                <button
                  type="button"
                  data-testid="filter-when-clear"
                  onClick={() => setFilters((f) => ({ ...f, around_datetime: "" }))}
                  className="text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-gray-900 rounded-sm px-2 py-1"
                >
                  Cancella
                </button>
              </div>
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">
                  Tolleranza ±{filters.time_window_hours}h
                </div>
                <input
                  data-testid="filter-time-window" type="range" min="1" max="12" step="1"
                  value={filters.time_window_hours}
                  onChange={(e) => setFilters({ ...filters, time_window_hours: Number(e.target.value) })}
                  className="w-full accent-[#E11D48]"
                  disabled={!filters.around_datetime}
                />
              </div>
            </div>

            <div>
              <label className="label-tag block mb-2">Tipo</label>
              <select
                data-testid="filter-ride-type"
                value={filters.ride_type}
                onChange={(e) => setFilters({ ...filters, ride_type: e.target.value })}
                className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-3 py-2.5 text-sm text-gray-900"
              >
                <option value="">Tutti i tipi</option>
                {catalog.ride_types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label className="label-tag block mb-2">Bici</label>
              <select
                data-testid="filter-bike-type"
                value={filters.bike_type}
                onChange={(e) => setFilters({ ...filters, bike_type: e.target.value })}
                className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-3 py-2.5 text-sm text-gray-900"
              >
                <option value="">Tutte</option>
                {catalog.bike_types.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </div>

            <div>
              <label className="label-tag block mb-2">Livello</label>
              <select
                data-testid="filter-target-level"
                value={filters.target_level}
                onChange={(e) => setFilters({ ...filters, target_level: e.target.value })}
                className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-3 py-2.5 text-sm text-gray-900"
              >
                <option value="">Tutti i livelli</option>
                {(catalog.levels || []).filter((l) => l.id !== "tutti").map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </div>

            <button
              data-testid="filter-reset"
              onClick={() => setFilters({ location: null, radius_km: 50, around_datetime: "", time_window_hours: 2, ride_type: "", bike_type: "", target_level: "", q: "" })}
              className="w-full text-xs uppercase tracking-widest font-bold text-gray-500 hover:text-gray-900 border border-gray-200 hover:border-gray-400 rounded-sm py-2"
            >
              Reset filtri
            </button>
          </aside>

          {/* Results + Map */}
          <section className="space-y-6 min-w-0">
            <div className="tactical-card rounded-sm overflow-hidden h-[300px]">
              <RideMap
                center={mapCenter}
                zoom={selectedLocation ? 9 : 5}
                radiusKm={selectedLocation ? filters.radius_km : 0}
                rides={rides}
              />
            </div>

            {loading ? (
              <div className="text-center py-12 text-gray-500">
                <div className="w-8 h-8 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <div className="label-tag">Caricamento...</div>
              </div>
            ) : rides.length === 0 ? (
              <div className="tactical-card rounded-sm p-12 text-center">
                <Search className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <div className="font-display font-bold text-xl text-gray-900">Nessun giro trovato</div>
                <p className="text-gray-500 mt-2 text-sm">Allarga il raggio o rimuovi qualche filtro.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {rides.map((r) => (
                  <RideCard
                    key={r.ride_id}
                    ride={r}
                    onUpdate={(fresh) => setRides((prev) => prev.map((x) => x.ride_id === fresh.ride_id ? { ...x, ...fresh } : x))}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </>
  );
}
